// Army stacks: the units following the player vs. units waiting at locations.
// Armies consume no food, money or other upkeep; only Command Points gate
// how many units the player can lead at once.

import type { ArmyStack, LocationId, LoopState, UnitId } from "../types"
import { getFactionById, getUnitById } from "./content"

function mergeIntoStacks(
  stacks: ArmyStack[],
  unitId: UnitId,
  count: number,
): ArmyStack[] {
  const existing = stacks.find((stack) => stack.unitId === unitId)
  if (existing) {
    return stacks.map((stack) =>
      stack.unitId === unitId ? { ...stack, count: stack.count + count } : stack,
    )
  }
  return [...stacks, { unitId, count }]
}

function removeFromStacks(
  stacks: ArmyStack[],
  unitId: UnitId,
  count: number,
): ArmyStack[] {
  return stacks
    .map((stack) =>
      stack.unitId === unitId ? { ...stack, count: stack.count - count } : stack,
    )
    .filter((stack) => stack.count > 0)
}

export function commandPointsUsed(army: ArmyStack[]): number {
  return army.reduce((sum, stack) => sum + stack.count, 0)
}

export function getStacksAtLocation(
  loop: LoopState,
  locationId: LocationId,
): ArmyStack[] {
  return loop.armiesByLocation[locationId] ?? []
}

export function takeUnits(
  loop: LoopState,
  locationId: LocationId,
  unitId: UnitId,
  count: number,
): LoopState {
  const waiting = getStacksAtLocation(loop, locationId)
  const available = waiting.find((stack) => stack.unitId === unitId)?.count ?? 0
  const taken = Math.min(count, available)
  if (taken <= 0) return loop
  if (commandPointsUsed(loop.currentArmy) + taken > loop.commandPoints) {
    throw new Error("Not enough Command Points to take these units")
  }
  return {
    ...loop,
    armiesByLocation: {
      ...loop.armiesByLocation,
      [locationId]: removeFromStacks(waiting, unitId, taken),
    },
    currentArmy: mergeIntoStacks(loop.currentArmy, unitId, taken),
  }
}

export function leaveUnits(
  loop: LoopState,
  locationId: LocationId,
  unitId: UnitId,
  count: number,
): LoopState {
  const inArmy = loop.currentArmy.find((stack) => stack.unitId === unitId)?.count ?? 0
  const left = Math.min(count, inArmy)
  if (left <= 0) return loop
  const waiting = getStacksAtLocation(loop, locationId)
  return {
    ...loop,
    currentArmy: removeFromStacks(loop.currentArmy, unitId, left),
    armiesByLocation: {
      ...loop.armiesByLocation,
      [locationId]: mergeIntoStacks(waiting, unitId, left),
    },
  }
}

/**
 * Sends units back to the faction that lent them, per Phase 4's "release
 * units back to the faction". Command Points are freed immediately and the
 * units regroup at their faction's home location, so releasing is a
 * reversible act of returning borrowed soldiers rather than destroying them.
 * Units with no faction home (or an unknown faction) simply disband.
 */
export function releaseUnits(
  loop: LoopState,
  unitId: UnitId,
  count: number,
): LoopState {
  const inArmy = loop.currentArmy.find((stack) => stack.unitId === unitId)?.count ?? 0
  const released = Math.min(count, inArmy)
  if (released <= 0) return loop
  const withoutReleased: LoopState = {
    ...loop,
    currentArmy: removeFromStacks(loop.currentArmy, unitId, released),
  }
  const factionId = getUnitById(unitId)?.factionId
  const homeLocationId = factionId ? getFactionById(factionId)?.homeLocationId : null
  if (!homeLocationId) return withoutReleased
  return addUnitsAtLocation(withoutReleased, homeLocationId, unitId, released)
}

/** Where releaseUnits will send this unit, so the UI can tell the player before they commit. */
export function getReleaseDestination(unitId: UnitId): LocationId | null {
  const factionId = getUnitById(unitId)?.factionId
  return (factionId ? getFactionById(factionId)?.homeLocationId : null) ?? null
}

/** Adds units waiting at a location (e.g. a faction granting recruits once trusted), independent of the player's current army. */
export function addUnitsAtLocation(
  loop: LoopState,
  locationId: LocationId,
  unitId: UnitId,
  count: number,
): LoopState {
  if (count <= 0) return loop
  const waiting = getStacksAtLocation(loop, locationId)
  return {
    ...loop,
    armiesByLocation: {
      ...loop.armiesByLocation,
      [locationId]: mergeIntoStacks(waiting, unitId, count),
    },
  }
}

export function takeAllUnits(
  loop: LoopState,
  locationId: LocationId,
): LoopState {
  const waiting = getStacksAtLocation(loop, locationId)
  return waiting.reduce((current, stack) => {
    const remainingCapacity = current.commandPoints - commandPointsUsed(current.currentArmy)
    const takeCount = Math.min(stack.count, Math.max(remainingCapacity, 0))
    if (takeCount <= 0) return current
    return takeUnits(current, locationId, stack.unitId, takeCount)
  }, loop)
}
